import { Taxonomy } from "@common/categories";
import { FIELD_UPDATE_SOURCE } from "@common/constants";
import { EntityProfileType } from "@common/types/entity";
import { capitalizationRuleCategories } from "@common/taxSavingRules";
import {
    AccountSubtype,
    DefaultCashBasisAccountCodes,
} from "@common/constants/domains/accounting/accounts";
import { AccountRole } from "@common/types/domains/accounting/account";
import {
    AccountCodes,
    defaultCashChartOfAccounts,
    PartnershipAccountCodes,
    SCorpAccountCodes,
    SolePropEquityAccountCodes,
} from "@common/constants/domains/accounting/chartOfAccounts";
import {
    isCorporationLedger,
    isPartnershipLedger,
    isSolePropLedger,
} from "@common/constants/domains/accounting/helpers";

interface MappingTaxonomyToAccountCodeParams {
    entityType: EntityProfileType;
    deductionRate?: number;
    isIncomingTransaction: boolean;
    isPairedWithPersonalEntity: boolean;
    categorizationSource?: FIELD_UPDATE_SOURCE | null;
}

type EquityLedgerKind = "corporation" | "soleProp" | "partnership";

/**
 * Sharing this branch keeps code and role helpers' validation aligned.
 */
function resolveEquityLedgerKind(
    entityType: EntityProfileType,
): EquityLedgerKind {
    if (isCorporationLedger(entityType)) {
        return "corporation";
    }
    if (isSolePropLedger(entityType)) {
        return "soleProp";
    }
    if (isPartnershipLedger(entityType)) {
        return "partnership";
    }
    throw new Error("Unsupported entity type in ledger");
}

export const mapTransactionToWithdrawalOrContributionEquityAccount = ({
    entityType,
    isIncomingTransaction,
}: Pick<
    MappingTaxonomyToAccountCodeParams,
    "entityType" | "isIncomingTransaction"
>) => {
    switch (resolveEquityLedgerKind(entityType)) {
        case "corporation":
            return isIncomingTransaction
                ? SCorpAccountCodes.Contributions
                : SCorpAccountCodes.Distributions;
        case "soleProp":
            return isIncomingTransaction
                ? SolePropEquityAccountCodes.OwnerInvestments
                : SolePropEquityAccountCodes.OwnerDraws;
        case "partnership":
            return isIncomingTransaction
                ? PartnershipAccountCodes.Contributions
                : PartnershipAccountCodes.Distributions;
    }
};

/**
 * Validate legal type even though the role is identical across legal types,
 * preserving the code-based helper's failure behavior.
 */
export const mapTransactionToWithdrawalOrContributionEquityRole = ({
    entityType,
    isIncomingTransaction,
}: Pick<
    MappingTaxonomyToAccountCodeParams,
    "entityType" | "isIncomingTransaction"
>) => {
    resolveEquityLedgerKind(entityType);
    return isIncomingTransaction
        ? AccountRole.ownerContributions
        : AccountRole.ownerDraws;
};

export const FINANCIAL_ACCOUNT_GROUPS_SUPPORTING_TRANSFER_PAYMENTS = [
    AccountSubtype.bankAccount,
    AccountSubtype.paymentProcessor,
    AccountSubtype.creditCard,
    AccountSubtype.payrollAccount,
];

export type TaxonomyMappingFunction = (
    params: MappingTaxonomyToAccountCodeParams,
) => AccountCodes;

const taxonomyFederalTaxMapping: TaxonomyMappingFunction = (params) => {
    const { entityType } = params;
    if ([EntityProfileType.C_CORP].includes(entityType)) {
        return DefaultCashBasisAccountCodes.federalTaxes;
    }
    return mapTransactionToWithdrawalOrContributionEquityAccount(params);
};

export const TaxonomyToDefaultChartOfAccounts: Record<
    Exclude<Taxonomy, Taxonomy.transfer>,
    AccountCodes | TaxonomyMappingFunction
> = {
    [Taxonomy.income]: ({ isIncomingTransaction }) => {
        if (isIncomingTransaction) {
            return DefaultCashBasisAccountCodes.revenue;
        }
        return DefaultCashBasisAccountCodes.refundsAndReturns;
    },
    [Taxonomy.interest_income]: DefaultCashBasisAccountCodes.interestIncome,
    [Taxonomy.other_income]: DefaultCashBasisAccountCodes.otherIncome,
    [Taxonomy.advertising_marketing]:
        DefaultCashBasisAccountCodes.advertisingAndMarketing,
    [Taxonomy.client_gifts]:
        DefaultCashBasisAccountCodes.advertisingAndMarketing,
    [Taxonomy.auto_vehicle]: DefaultCashBasisAccountCodes.vehicle,
    [Taxonomy.auto_insurance]: DefaultCashBasisAccountCodes.vehicle,
    [Taxonomy.auto_payment]: DefaultCashBasisAccountCodes.vehicleLeases,
    [Taxonomy.parking_tolls]:
        DefaultCashBasisAccountCodes.vehicleParkingAndTolls,
    [Taxonomy.communications]: DefaultCashBasisAccountCodes.internetAndPhone,
    [Taxonomy.learning_development]:
        DefaultCashBasisAccountCodes.learningAndDevelopment,
    [Taxonomy.entertainment]: DefaultCashBasisAccountCodes.entertainment,
    [Taxonomy.fees]: DefaultCashBasisAccountCodes.legalAndProfessionalFees,
    [Taxonomy.loan_fees]: DefaultCashBasisAccountCodes.loanFees,
    [Taxonomy.licenses_permits]:
        DefaultCashBasisAccountCodes.licensesAndPermits,
    [Taxonomy.merchant_bank_fees]:
        DefaultCashBasisAccountCodes.merchantAndBankFees,
    [Taxonomy.penalties_fines]: DefaultCashBasisAccountCodes.finesAndPenalties,
    [Taxonomy.insurance]: DefaultCashBasisAccountCodes.insuranceBusiness,
    [Taxonomy.health_insurance]:
        DefaultCashBasisAccountCodes.insuranceOwnersPartnersOfficers,
    [Taxonomy.life_insurance]:
        DefaultCashBasisAccountCodes.insuranceOwnersPartnersOfficers,
    [Taxonomy.legal_professional]:
        DefaultCashBasisAccountCodes.legalAndProfessionalFees,
    [Taxonomy.meals]: ({ deductionRate }) => {
        if (deductionRate === 1) {
            return DefaultCashBasisAccountCodes.mealsEmployeeCatering;
        }
        return DefaultCashBasisAccountCodes.meals;
    },
    [Taxonomy.payroll]: ({ categorizationSource }) =>
        categorizationSource === FIELD_UPDATE_SOURCE.GUSTO
            ? DefaultCashBasisAccountCodes.salariesAndWagesClearing
            : DefaultCashBasisAccountCodes.contractLabor,
    [Taxonomy.employee_benefits]: DefaultCashBasisAccountCodes.employeeBenefits,
    [Taxonomy.retirement_benefits]:
        DefaultCashBasisAccountCodes.retirementBenefits,
    [Taxonomy.contractors]: DefaultCashBasisAccountCodes.contractLabor,
    [Taxonomy.salaries_wages]: DefaultCashBasisAccountCodes.salariesAndWages,
    [Taxonomy.payroll_taxes]: DefaultCashBasisAccountCodes.payrollTaxes,
    [Taxonomy.rentals_leases]: DefaultCashBasisAccountCodes.rentAndLease,
    [Taxonomy.repairs_maintenance]:
        DefaultCashBasisAccountCodes.facilitiesRepairsAndMaintenance,
    [Taxonomy.software_subscriptions]:
        DefaultCashBasisAccountCodes.softwareAndSubscriptions,
    [Taxonomy.taxes]: taxonomyFederalTaxMapping,
    [Taxonomy.federal_taxes]: taxonomyFederalTaxMapping,
    [Taxonomy.local_taxes]: DefaultCashBasisAccountCodes.stateAndLocalTaxes,
    [Taxonomy.property_taxes]: DefaultCashBasisAccountCodes.propertyTaxes,
    [Taxonomy.sales_taxes]: DefaultCashBasisAccountCodes.salesTaxPayable, // make additional rule
    [Taxonomy.travel]: DefaultCashBasisAccountCodes.travel,
    [Taxonomy.utilities]: DefaultCashBasisAccountCodes.officeUtilities,

    [Taxonomy.supplies_equipment]:
        DefaultCashBasisAccountCodes.suppliesAndEquipment,
    [Taxonomy.business_office]: DefaultCashBasisAccountCodes.rentAndLease,
    [Taxonomy.mortgage_payment]: DefaultCashBasisAccountCodes.longTermLoans,
    [Taxonomy.equipment_rentals]:
        DefaultCashBasisAccountCodes.suppliesAndEquipment,
    [Taxonomy.office_postage]: DefaultCashBasisAccountCodes.officeExpenses,

    [Taxonomy.furniture]:
        DefaultCashBasisAccountCodes.furnitureAndFixturesExpense,
    [Taxonomy.props_production]:
        DefaultCashBasisAccountCodes.suppliesAndEquipment,
    [Taxonomy.credit_card_loans]:
        DefaultCashBasisAccountCodes.creditCardClearing,
    [Taxonomy.line_of_credit]: DefaultCashBasisAccountCodes.lineOfCredit,
    [Taxonomy.credit_card_payment]: (params) => {
        const { isPairedWithPersonalEntity } = params;
        if (isPairedWithPersonalEntity) {
            return mapTransactionToWithdrawalOrContributionEquityAccount(
                params,
            );
        }
        return DefaultCashBasisAccountCodes.creditCardClearing;
    },
    [Taxonomy.interest_paid]: DefaultCashBasisAccountCodes.interestExpense,
    [Taxonomy.loan_payments_proceeds]:
        DefaultCashBasisAccountCodes.longTermLoans,
    [Taxonomy.contributions_distributions]:
        mapTransactionToWithdrawalOrContributionEquityAccount,
    [Taxonomy.improvements]: DefaultCashBasisAccountCodes.leaseholdImprovements,
    [Taxonomy.property_insurance]:
        DefaultCashBasisAccountCodes.insuranceBusiness,
    [Taxonomy.management_fees]:
        DefaultCashBasisAccountCodes.facilitiesManagementFees,
    [Taxonomy.health_dental]:
        mapTransactionToWithdrawalOrContributionEquityAccount,
    [Taxonomy.donations]: (params) => {
        const { entityType } = params;
        if (
            isCorporationLedger(entityType) ||
            isPartnershipLedger(entityType)
        ) {
            return DefaultCashBasisAccountCodes.donations;
        }
        return mapTransactionToWithdrawalOrContributionEquityAccount(params);
    },
    [Taxonomy.uncategorized]: ({ isIncomingTransaction }) =>
        isIncomingTransaction
            ? DefaultCashBasisAccountCodes.uncategorizedIncome
            : DefaultCashBasisAccountCodes.uncategorizedExpenses,
    [Taxonomy.retirement]: DefaultCashBasisAccountCodes.employeeBenefits, // clarify
    [Taxonomy.personal]: mapTransactionToWithdrawalOrContributionEquityAccount,
    [Taxonomy.security_deposit]: DefaultCashBasisAccountCodes.securityDeposits,
    [Taxonomy.principal_paid]: DefaultCashBasisAccountCodes.longTermLoans,
    [Taxonomy.home_office]:
        mapTransactionToWithdrawalOrContributionEquityAccount, // clarify
    [Taxonomy.cogs]: DefaultCashBasisAccountCodes.cogsGoodsAndMaterials,
    [Taxonomy.direct_labor]: DefaultCashBasisAccountCodes.cogsLabor,
    [Taxonomy.goods_materials]:
        DefaultCashBasisAccountCodes.cogsGoodsAndMaterials,
    [Taxonomy.other_cogs]: DefaultCashBasisAccountCodes.cogsOther,
    [Taxonomy.investment]: DefaultCashBasisAccountCodes.investments,
    [Taxonomy.dividend_income]: DefaultCashBasisAccountCodes.dividendIncome,
    [Taxonomy.charitable_contributions]: (params) => {
        const { entityType } = params;

        if (isSolePropLedger(entityType)) {
            return SolePropEquityAccountCodes.OwnerDraws;
        }

        return DefaultCashBasisAccountCodes.donations;
    },

    [Taxonomy.assets]: DefaultCashBasisAccountCodes.otherFixedAssets,
    [Taxonomy.escrow]: DefaultCashBasisAccountCodes.otherCurrentAssets,
    [Taxonomy.computers]: DefaultCashBasisAccountCodes.equipment,
    [Taxonomy.furniture_fixtures]:
        DefaultCashBasisAccountCodes.furnitureAndFixturesAsset,
    [Taxonomy.machinery_equipment]: DefaultCashBasisAccountCodes.equipment,
    [Taxonomy.vehicles]: DefaultCashBasisAccountCodes.vehicles,
    [Taxonomy.buildings]: DefaultCashBasisAccountCodes.buildings,
    [Taxonomy.land]: DefaultCashBasisAccountCodes.land,
};

export const TaxonomyToDefaultChartOfAccountsForCapitalizationRule: Record<
    (typeof capitalizationRuleCategories)[number],
    keyof typeof defaultCashChartOfAccounts | null
> = {
    [Taxonomy.supplies_equipment]: DefaultCashBasisAccountCodes.equipment,
    [Taxonomy.furniture_fixtures]:
        DefaultCashBasisAccountCodes.furnitureAndFixturesAsset,
    [Taxonomy.props_production]: DefaultCashBasisAccountCodes.equipment,
    [Taxonomy.auto_vehicle]: DefaultCashBasisAccountCodes.vehicles,
    [Taxonomy.repairs_maintenance]: DefaultCashBasisAccountCodes.buildings,
    [Taxonomy.management_fees]: DefaultCashBasisAccountCodes.buildings,
};

export const getFinancialAccountGroupsSupportingTransferPayments = () =>
    FINANCIAL_ACCOUNT_GROUPS_SUPPORTING_TRANSFER_PAYMENTS;

export const getTaxonomyToDefaultChartOfAccountsMapping = () =>
    TaxonomyToDefaultChartOfAccounts;

interface GetAccountCodeForTaxonomyParams {
    taxonomy: Taxonomy;
    isIncomingTransaction: boolean;
    entityType: EntityProfileType | null;
    deductionRate?: number;
    categorizationSource?: FIELD_UPDATE_SOURCE | null;
}

export function getAccountCodeForTaxonomy({
    taxonomy,
    isIncomingTransaction,
    entityType,
    deductionRate,
    categorizationSource = null,
}: GetAccountCodeForTaxonomyParams): number | null {
    if (taxonomy === Taxonomy.transfer) {
        return null;
    }

    const mapping = getTaxonomyToDefaultChartOfAccountsMapping();
    const entry = mapping[taxonomy];

    if (typeof entry === "number") {
        return entry;
    }

    if (!entityType) {
        return null;
    }

    try {
        return entry({
            entityType,
            isIncomingTransaction,
            deductionRate,
            isPairedWithPersonalEntity: false,
            categorizationSource,
        });
    } catch {
        return null;
    }
}
